import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FaInstagram, FaYoutube, FaFacebookF, FaTwitter, FaWhatsapp } from 'react-icons/fa';
import API_BASE_URL from '../services/apiconfig';
import { useLanguage } from '../context/LanguageContext';

const HomePage = () => {
  const { language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselImages, setCarouselImages] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';

  const content = {
    en: {
      loading: 'Loading...',
      noBanners: 'No banner images available',
      clickToOpen: 'Click here to open notification',
      new: 'New',
    },
    ta: {
      loading: 'ஏற்றுகிறது...',
      noBanners: 'பேனர் படங்கள் கிடைக்கவில்லை',
      clickToOpen: 'அறிவிப்பைத் திறக்க இங்கே கிளிக் செய்யவும்',
      new: 'புதியது',
    }
  };

  // Announcements shown as running text — each opens its link on click
  const announcements = [
    {
      pdf: '/documents/Store_Name_and_Address_List.pdf',
      en: 'Grade ‘A’ quality fine rice is being sold at ₹30 per kg, with a limit of 5 kg per family, through 19 selected Cooperative Stores in Chennai. Click here to view the store list.',
      ta: 'சென்னையில் தேர்வு செய்யப்பட்ட 19 கூட்டுறவு அங்காடிகள் மூலம் "A" தர சன்ன ரக அரிசி குடும்பம் ஒன்றுக்கு 5 கிலோ வீதம் ஒரு கிலோ ரூ.30/- என்ற விலையில் விற்பனை செய்யப்படுகிறது. கடைகளின் பட்டியலை காண இங்கு கிளிக் செய்யவும்.',
    },
  ];

  const t = content[language];

  const isVideo = (filePath) => {
    return filePath?.toLowerCase().endsWith('.mp4') || 
           filePath?.toLowerCase().endsWith('.webm') ||
           filePath?.toLowerCase().endsWith('.mov');
  };

  // Fetch active banner images from API
  useEffect(() => {
    const fetchBannerImages = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/common_upload/get_active_image_path`);
        const result = await response.json();
        
        if (result.status === 'success' && result.data && Array.isArray(result.data)) {
          const images = result.data.map(item => `${MEDIA_BASE_URL}/${item.main_image_path}`);
          setCarouselImages(images);
        }
      } catch (error) {
        console.error('Error fetching banner images:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBannerImages();
  }, []);

  // Fetch stats data from API
  useEffect(() => {
    const fetchStatsData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/flexible_contents/get_main_stats_contents`);
        const result = await response.json();
        
        if (result.status === 'success' && result.data) {
          setStatsData(result.data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStatsData();
  }, []);

  useEffect(() => {
    if (carouselImages.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
      }, 8000);

      return () => clearInterval(interval);
    }
  }, [carouselImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  // Handle external social media links
  const handleSocialClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">{t.loading}</div>
      </div>
    );
  }

  if (carouselImages.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">{t.noBanners}</div>
      </div>
    );
  }

  return (
    <div id="main-content" className="bg-gray-50">
      
      {/* Carousel Section - Responsive aspect-ratio on mobile, fixed height on desktop */}
      <section className="relative w-full aspect-[16/9] sm:aspect-auto sm:h-[350px] lg:h-[500px] overflow-hidden bg-gray-900">
        {/* Carousel track */}
        <div
          className="w-full h-full flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {carouselImages.map((image, index) => (
            <div key={index} className="relative w-full h-full flex-shrink-0">
              {isVideo(image) ? (
                <video
                  src={image}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover sm:object-fill"
                />
              ) : (
                <img
                  src={image}
                  alt={`Banner ${index + 1}`}
                  className="w-full h-full object-cover sm:object-fill"
                />
              )}
            </div>
          ))}
        </div>

        {/* Social Media Vertical Bar */}
        <div className="hidden sm:block absolute left-2 sm:left-3 lg:left-6 top-1/2 -translate-y-1/2 z-30">
          <div className="relative bg-blue-700/25 border border-blue-500/30 backdrop-blur-md rounded-3xl px-2 sm:px-3 lg:px-4 py-4 sm:py-6 lg:py-10 shadow-2xl border border-gray-300/30">
            <div className="flex flex-col gap-3 sm:gap-4 lg:gap-6">
              
              <button
                onClick={() => handleSocialClick('https://instagram.com/tncoopdept')}
                className="group relative w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110"
              >
                <FaInstagram className="text-white text-sm sm:text-base lg:text-lg transition-transform duration-300 group-hover:scale-110" />
                <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </button>

              <button
                onClick={() => handleSocialClick('https://www.youtube.com/@tncoopdept')}
                className="group relative w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110"
              >
                <FaYoutube className="text-white text-sm sm:text-base lg:text-lg transition-transform duration-300 group-hover:scale-110" />
                <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </button>

              <button
                onClick={() => handleSocialClick('https://facebook.com/profile.php?id=100093222344544')}
                className="group relative w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110"
              >
                <FaFacebookF className="text-white text-sm sm:text-base lg:text-lg transition-transform duration-300 group-hover:scale-110" />
                <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </button>

              <button
                onClick={() => handleSocialClick('https://x.com/TNCOOPDEPT')}
                className="group relative w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-gray-800 to-black rounded-xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110"
              >
                <FaTwitter className="text-white text-sm sm:text-base lg:text-lg transition-transform duration-300 group-hover:scale-110" />
                <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </button>

              <button
                onClick={() => handleSocialClick('https://whatsapp.com/channel/0029Vb6POeTGE56dJATiYc2v')}
                className="group relative w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110"
              >
                <FaWhatsapp className="text-white text-sm sm:text-base lg:text-lg transition-transform duration-300 group-hover:scale-110" />
                <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </button>

            </div>
          </div>
        </div>


        {/* Dot Navigation */}
        <div className="absolute bottom-2 sm:bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2 z-30">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all ${
                currentSlide === index
                  ? "w-6 sm:w-10 h-1.5 sm:h-2.5 bg-white rounded-full"
                  : "w-1.5 sm:w-2.5 h-1.5 sm:h-2.5 bg-white/60 hover:bg-white/80 rounded-full"
              }`}
            />
          ))}
        </div>
      </section>

      {/* Announcements Running Text - separate section, each opens PDF on click */}
      <section className="bg-gray-50 py-4 sm:py-5 px-3 sm:px-6">
        <div className="w-full max-w-6xl mx-auto flex flex-col gap-3">
          {announcements.map((a, idx) => {
            const text = language === 'en' ? a.en : a.ta;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => window.open(a.pdf, '_blank', 'noopener,noreferrer')}
                className="w-full flex items-center bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:from-blue-800 hover:to-blue-600 transition-colors py-3"
                aria-label={text}
              >
                <span className="flex-shrink-0 bg-red-600 text-white text-xs font-bold uppercase px-3 py-1 rounded-r-full mr-4 tracking-wide">
                  {t.new}
                </span>
                <div className="relative overflow-hidden flex-1">
                  <div className="whitespace-nowrap animate-announcement">
                    <span className="text-sm sm:text-base font-medium px-8">📢 {text}</span>
                    <span className="text-sm sm:text-base font-medium px-8">📢 {text}</span>
                  </div>
                </div>
                <span className="flex-shrink-0 bg-white/20 text-white text-xs sm:text-sm font-semibold px-3 sm:px-4 py-1.5 rounded-l-full ml-4 whitespace-nowrap underline underline-offset-2">
                  {t.clickToOpen}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Stats Section - Marquee with API Data */}
      <section className="bg-white py-6 sm:py-8">
        <div className="w-full px-2">
          
          {statsLoading ? (
            <div className="flex justify-center py-8">
              <div className="text-gray-600">{t.loading}</div>
            </div>
          ) : statsData.length === 0 ? (
            <div className="flex justify-center py-8">
              <div className="text-gray-600">No statistics available</div>
            </div>
          ) : (
            <>
              {/* Mobile: single col list */}
              <div className="sm:hidden grid grid-cols-1 gap-2 px-4 max-h-[calc(4*100px)] overflow-y-auto">
                {statsData.map((item, i) => {
                  const gradients = [
                    "from-blue-100 to-blue-200","from-indigo-100 to-indigo-200",
                    "from-teal-100 to-teal-200","from-slate-200 to-slate-300",
                    "from-sky-100 to-sky-200","from-purple-100 to-purple-200",
                    "from-cyan-100 to-cyan-200","from-violet-100 to-violet-200"
                  ];
                  return (
                    <div key={i} className={`bg-gradient-to-br ${gradients[i % gradients.length]} rounded-xl px-4 py-5 shadow-sm flex flex-col items-center justify-center text-center min-h-[90px] border border-gray-200`}>
                      <div className="text-xl font-black text-gray-900 leading-tight">
                        {language === 'en' ? item.heading_eng : item.heading_tamil}
                      </div>
                      <div className="text-xs text-gray-600 font-medium mt-0.5">
                        {language === 'en' ? item.content_eng : item.content_tamil}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* sm+: scrolling marquee */}
              <div className="relative overflow-hidden py-2 hidden sm:block">
              <div className="marquee-content flex gap-6 animate-marquee">
                {/* Duplicate the array twice for seamless loop */}
                {[...Array(2)].map((_, setIndex) => (
                  <React.Fragment key={setIndex}>
                    {statsData.map((item, i) => {
                      const gradients = [
                        "from-blue-100 to-blue-200",
                        "from-indigo-100 to-indigo-200",
                        "from-teal-100 to-teal-200",
                        "from-slate-200 to-slate-300",
                        "from-sky-100 to-sky-200",
                        "from-purple-100 to-purple-200",
                        "from-cyan-100 to-cyan-200",
                        "from-violet-100 to-violet-200"
                      ];

                      return (
                        <div
                          key={i}
                          className={`bg-gradient-to-br ${gradients[i % gradients.length]} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 hover:scale-105 border border-gray-300 min-w-[200px] flex-shrink-0`}
                        >
{/* Icon */}
<div
  className="w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto"
  style={{
    background: [
      'linear-gradient(135deg, #1e3a5f, #2563eb)',
      'linear-gradient(135deg, #312e81, #6366f1)',
      'linear-gradient(135deg, #134e4a, #14b8a6)',
      'linear-gradient(135deg, #4a1d6a, #a855f7)',
      'linear-gradient(135deg, #1e3a5f, #0ea5e9)',
      'linear-gradient(135deg, #064e3b, #10b981)',
      'linear-gradient(135deg, #78350f, #f59e0b)',
      'linear-gradient(135deg, #7f1d1d, #ef4444)',
    ][i % 8],
    boxShadow: `0 4px 14px ${['rgba(37,99,235,0.3)', 'rgba(99,102,241,0.3)', 'rgba(20,184,166,0.3)', 'rgba(168,85,247,0.3)', 'rgba(14,165,233,0.3)', 'rgba(16,185,129,0.3)', 'rgba(245,158,11,0.3)', 'rgba(239,68,68,0.3)'][i % 8]}`
  }}
>
  {item.image ? (
    <img
      src={`${MEDIA_BASE_URL}/${item.image}`}
      alt=""
      className="w-7 h-7 object-contain brightness-0 invert"
    />
  ) : (
    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      {[
        <><rect x="3" y="12" width="4" height="9" rx="1" /><rect x="10" y="7" width="4" height="14" rx="1" /><rect x="17" y="3" width="4" height="18" rx="1" /></>,
        <><circle cx="9" cy="7" r="3" /><circle cx="17" cy="9" r="2" /><path d="M2 21v-1a5 5 0 015-5h4a5 5 0 015 5v1" /><path d="M17 14a3 3 0 013 3v4" /></>,
        <><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-4h6v4" /><circle cx="9" cy="11" r=".5" fill="currentColor" /><circle cx="15" cy="11" r=".5" fill="currentColor" /></>,
        <><circle cx="12" cy="12" r="9" /><ellipse cx="12" cy="12" rx="3.5" ry="9" /><line x1="3" y1="12" x2="21" y2="12" /></>,
        <><circle cx="12" cy="12" r="9" /><path d="M14.5 9.5a2.5 2.5 0 00-2.5-2h-1a2.5 2.5 0 000 5h2a2.5 2.5 0 010 5h-1a2.5 2.5 0 01-2.5-2" /><line x1="12" y1="5.5" x2="12" y2="7" /><line x1="12" y1="17" x2="12" y2="18.5" /></>,
        <><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" /></>,
        <><path d="M12 2l7 4v5c0 5.25-3.5 9.74-7 11-3.5-1.26-7-5.75-7-11V6l7-4z" /><path d="M9 12l2 2 4-4" /></>,
        <><path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.66 0 3-4.03 3-9s-1.34-9-3-9m0 18c-1.66 0-3-4.03-3-9s1.34-9 3-9" /></>,
      ][i % 8]}
    </svg>
  )}
</div>
                          {/* Value */}
                          <div className="text-3xl font-extrabold text-gray-800 mb-2 text-center tracking-tight">
                            {language === 'en' ? item.heading_eng : item.heading_tamil}
                          </div>

                          {/* Label */}
                          <div className="text-sm text-gray-700 font-semibold leading-tight text-center">
                            {language === 'en' ? item.content_eng : item.content_tamil}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
              </div>
            </>
          )}
        </div>

        {/* Marquee Animation */}
        <style jsx>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 40s linear infinite;
          }
          .marquee-content:hover {
            animation-play-state: paused;
          }
          @keyframes announcement {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-announcement {
            display: inline-block;
            animation: announcement 30s linear infinite;
          }
          .animate-announcement:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>
    </div>
  );
};

export default HomePage;